import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { LifeBuoy, X, MessageSquare, HelpCircle, Send, ChevronDown, Inbox, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { createTicket, getTickets, getTicket, postTicketMessage } from "@/lib/afmApi";
import { fmtDate } from "@/lib/format";

const FAQS = [
  { q: "¿Cuánto tarda mi música en llegar a las tiendas?", a: "La aprobación es automática, en un máximo de 72 horas tras el envío. Después, tu música aparece normalmente entre 3 y 7 días hábiles; tiendas como Spotify y Apple Music pueden ser más rápidas." },
  { q: "¿Cómo se calculan mis regalías?", a: "El reparto es 70% artista / 30% SONA, sin retención fiscal en esta fase. El neto ya incluye la distribución automática por Split Sheet entre colaboradores." },
  { q: "¿Cuándo puedo retirar mis fondos?", a: "A partir de USD 50 disponibles. Las solicitudes se procesan según el método elegido (banco, PayPal, cripto o bolívares)." },
  { q: "¿Qué requisitos debe cumplir mi portada?", a: "3000×3000 px, formato JPG/PNG en RGB, sin logos de terceros ni texto promocional. Usa la herramienta de Carátulas para dejarla lista." },
  { q: "¿Por qué no veo reproducciones ni regalías todavía?", a: "Las cifras aparecen cuando llegan los primeros reportes reales de las tiendas. No mostramos números de ejemplo." },
];

const CATEGORIES = [
  { key: "distribution", label: "Entregas y distribución" },
  { key: "billing", label: "Pagos y regalías" },
  { key: "other", label: "Otro" },
];

const TSTATUS = {
  pending: "Pendiente", open: "Abierto", in_progress: "En proceso",
  resolved: "Resuelto", closed: "Cerrado",
};
const statusLabel = (s) => TSTATUS[s] || s || "—";

export const SupportWidget = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("afm_support_dismissed") === "1");
  const [tab, setTab] = useState("faq");
  const [openFaq, setOpenFaq] = useState(0);

  // New ticket
  const [category, setCategory] = useState("distribution");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Tickets list + detail
  const [tickets, setTickets] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);

  const loadList = useCallback(() => {
    if (!user?.id) return;
    setLoadingList(true);
    getTickets(user.id).then((d) => setTickets(Array.isArray(d) ? d : [])).catch(() => setTickets([])).finally(() => setLoadingList(false));
  }, [user?.id]);

  const openDetail = (id) => {
    setLoadingDetail(true);
    setDetail(null);
    getTicket(id, user.id).then(setDetail).catch(() => toast.error("No se pudo abrir el ticket")).finally(() => setLoadingDetail(false));
  };

  useEffect(() => {
    if (!open) { setSubject(""); setMessage(""); setTab("faq"); setDetail(null); setReply(""); }
  }, [open]);

  useEffect(() => {
    if (open && tab === "tickets" && !detail) loadList();
  }, [open, tab, detail, loadList]);

  const submit = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Completa asunto y mensaje"); return; }
    setSending(true);
    try {
      const t = await createTicket({ user_id: user?.id, subject: subject.trim(), category, initial_message: message.trim() });
      toast.success("Mensaje enviado al equipo de soporte.");
      setSubject(""); setMessage("");
      setTab("tickets");
      if (t?.id) openDetail(t.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el mensaje");
    } finally { setSending(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setReplying(true);
    try {
      await postTicketMessage(detail.id, { from_user_id: user.id, body: reply.trim() });
      setReply("");
      openDetail(detail.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar la respuesta");
    } finally { setReplying(false); }
  };

  const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black";
  const TabBtn = ({ id, icon: Icon, children }) => (
    <button data-testid={`support-tab-${id}`} onClick={() => { setTab(id); setDetail(null); }} className={`flex items-center gap-2 rounded-t-lg px-4 py-2.5 text-sm font-semibold ${tab === id ? "border-b-2 border-black text-black" : "text-slate-500"}`}>
      <Icon className="h-4 w-4" /> {children}
    </button>
  );

  return (
    <>
      {!open && (dismissed ? (
        <button
          data-testid="support-fab-mini"
          onClick={() => { setDismissed(false); localStorage.setItem("afm_support_dismissed", "0"); }}
          aria-label="Mostrar soporte"
          className="fixed bottom-5 right-5 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur transition-colors hover:bg-black"
        >
          <LifeBuoy className="h-5 w-5" />
        </button>
      ) : (
        <div className="fixed bottom-5 right-5 z-40 flex items-center gap-1.5">
          <button
            data-testid="support-fab"
            onClick={() => setOpen(true)}
            className="flex items-center gap-2 rounded-full bg-black px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(0,0,0,0.2)] transition-transform hover:scale-105"
          >
            <LifeBuoy className="h-5 w-5" /> <span className="hidden sm:inline">Soporte &amp; Ayuda</span>
          </button>
          <button
            data-testid="support-dismiss"
            onClick={() => { setDismissed(true); localStorage.setItem("afm_support_dismissed", "1"); }}
            aria-label="Ocultar soporte"
            className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:text-black"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="support-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Always Fresh Music</div>
                <h2 className="font-serif text-xl font-bold text-black">Soporte & Ayuda</h2>
              </div>
              <button onClick={() => setOpen(false)} data-testid="close-support"><X className="h-5 w-5 text-slate-400 hover:text-black" /></button>
            </div>

            <div className="flex gap-1 border-b border-slate-200 px-4 pt-3">
              <TabBtn id="faq" icon={HelpCircle}>FAQ</TabBtn>
              <TabBtn id="contact" icon={MessageSquare}>Nuevo</TabBtn>
              <TabBtn id="tickets" icon={Inbox}>Mis tickets</TabBtn>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {tab === "faq" && (
                <div className="space-y-3">
                  {FAQS.map((f, i) => (
                    <div key={i} className="rounded-xl border border-slate-200" data-testid={`faq-item-${i}`}>
                      <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-black">
                        {f.q}
                        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                      </button>
                      {openFaq === i && <p className="border-t border-slate-100 px-4 py-3 text-sm leading-relaxed text-slate-600">{f.a}</p>}
                    </div>
                  ))}
                </div>
              )}

              {tab === "contact" && (
                <div className="space-y-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Categoría</label>
                    <select data-testid="support-category" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
                      {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Asunto</label>
                    <input data-testid="support-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Resumen de tu consulta" className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Mensaje</label>
                    <textarea data-testid="support-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Cuéntanos en qué podemos ayudarte…" className={`${inputCls} resize-none`} />
                  </div>
                  <button data-testid="support-submit" onClick={submit} disabled={sending} className="flex w-full items-center justify-center gap-2 rounded-lg bg-black px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
                    {sending ? "Enviando…" : <><Send className="h-4 w-4" /> Enviar mensaje</>}
                  </button>
                </div>
              )}

              {tab === "tickets" && !detail && (
                <div className="space-y-3" data-testid="support-tickets-list">
                  {loadingList ? (
                    <div className="flex justify-center py-10"><span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-black" /></div>
                  ) : tickets.length === 0 ? (
                    <p className="py-10 text-center text-sm text-slate-400">Aún no tienes tickets. Crea uno en la pestaña "Nuevo".</p>
                  ) : (
                    tickets.map((t) => (
                      <button key={t.id} data-testid={`support-ticket-${t.id}`} onClick={() => openDetail(t.id)} className="flex w-full items-start justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3 text-left hover:border-black">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-black">{t.subject}</div>
                          <div className="mt-0.5 text-xs text-slate-500">{fmtDate(t.created_at)}</div>
                        </div>
                        <span className="shrink-0 rounded-full border border-slate-300 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600">{statusLabel(t.status)}</span>
                      </button>
                    ))
                  )}
                </div>
              )}

              {tab === "tickets" && detail && (
                <div className="space-y-4" data-testid="support-ticket-detail">
                  <button onClick={() => setDetail(null)} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-black"><ArrowLeft className="h-4 w-4" /> Volver</button>
                  <div className="border-b border-slate-100 pb-3">
                    <div className="font-serif text-lg font-bold text-black">{detail.subject}</div>
                    <span className="mt-1 inline-block rounded-full border border-slate-300 bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">{statusLabel(detail.status)}</span>
                  </div>
                  {loadingDetail ? (
                    <div className="flex justify-center py-8"><span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-black" /></div>
                  ) : (
                    <div className="space-y-3">
                      {(detail.messages || []).map((m) => {
                        const mine = m.from_user_id === user.id;
                        return (
                          <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`ticket-message-${m.id}`}>
                            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-black text-white" : "border border-slate-200 bg-slate-50 text-slate-800"}`}>
                              <div className={`mb-0.5 text-[10px] font-semibold uppercase tracking-wider ${mine ? "text-slate-300" : "text-slate-400"}`}>{m.from_name || (mine ? "Tú" : "Soporte")}</div>
                              <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {detail.status !== "closed" && (
                    <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
                      <textarea data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Escribe una respuesta…" className={`${inputCls} resize-none`} />
                      <button data-testid="ticket-reply-send" onClick={sendReply} disabled={replying || !reply.trim()} className="shrink-0 rounded-lg bg-black p-3 text-white disabled:opacity-50"><Send className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
